import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'
import AssetDownload from '@comp/product/asset-download'

const pillars: Pillar[] = [
  {
    title: 'Native tools, brokered access',
    description:
      'Engineers keep kubectl and oc. Monopam issues a short-lived kubeconfig pointing at its gateway, injects the real cluster credential, and the user never sees it.',
    bullets: [
      'Works with unmodified kubectl and oc',
      'Short-lived kubeconfig per session',
      'Cluster credentials injected, never exposed',
      'Native oc login supported for OpenShift',
    ],
  },
  {
    title: 'Policy at the API-call level',
    description:
      'Every request through the gateway is evaluated against verb, namespace, and resource rules: the same allow / approval / deny model you already run for servers.',
    bullets: [
      'Verb, namespace, and resource-level rules',
      'Deny > approval > allow precedence, RBAC-aligned',
      'Approval flows for sensitive operations',
      'OpenShift API groups covered (Projects, Routes, DeploymentConfigs)',
    ],
  },
  {
    title: 'Zero standing privilege, by construction',
    description:
      'Tokens are minted per session via the Kubernetes TokenRequest API and expire with it. No long-lived kubeconfigs in laptops, wikis, or CI variables.',
    bullets: [
      'Just-in-time tokens per session',
      'Session never outlives the token',
      'No shared cluster-admin kubeconfigs',
      'Time-bound, approval-gated elevation',
    ],
  },
  {
    title: 'Recorded like every privileged session',
    description:
      'kubectl exec is a privileged shell; Monopam treats it like one. Terminal sessions are recorded; every other API call lands in a structured audit trail.',
    bullets: [
      'kubectl exec / attach terminal recording',
      'Structured JSON audit for API calls',
      'Searchable trail per user, cluster, namespace',
      'Same audit surface as RDP / SSH sessions',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Register the cluster',
    description:
      'Connect Kubernetes or OpenShift and vault the cluster credential. The gateway becomes the audited path in.',
  },
  {
    title: 'Write the policy',
    description:
      'Scope access by verb, namespace, and resource. Decide what runs, what waits for approval, and what never happens.',
  },
  {
    title: 'Hand out nothing',
    description:
      'Engineers request a session and get a short-lived kubeconfig. Access expires on its own; the recording stays.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Monopam Gateway',
    description:
      'The brokered, audited channel every session runs through.',
    href: '/pam/gateway',
  },
  {
    label: 'Just-in-time access',
    description:
      'Time-bound, approval-driven privilege: the model K8s access follows.',
    href: '/pam/jit-access',
  },
  {
    label: 'Use case: DevOps privileged access',
    description:
      'Clusters, databases, and servers: one brokered model.',
    href: '/solutions/devops-privileged-access',
  },
]

export default function KubernetesPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Kubernetes & OpenShift privileged access"
        description="PAM for Kubernetes and OpenShift: short-lived kubeconfigs, credential injection, verb/namespace/resource policy, kubectl exec recording, and just-in-time tokens, all with native kubectl and oc."
        path="/pam/kubernetes"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Kubernetes access"
          title={
            <>
              cluster-admin is root.
              <br />
              Treat it like root.
            </>
          }
          body="Most PAM products stop at the SSH port. Monopam brokers Kubernetes and OpenShift access the same way it brokers servers: short-lived credentials, per-call policy, recorded sessions, and your engineers keep using kubectl."
          badges={[
            'Native kubectl & oc',
            'Verb / namespace / resource policy',
            'JIT tokens, zero standing privilege',
          ]}
        />
        <SolutionPillars
          title={<>The cluster is a privileged surface. Front it.</>}
          body="A kubeconfig with cluster-admin is a domain-admin password that lives in dotfiles. Monopam replaces it with brokered, expiring, recorded access."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to governed cluster access.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <AssetDownload
          asset="k8s-guide"
          title={{
            en: 'Securing Kubernetes access: the field guide.',
            tr: 'Kubernetes erişim güvenliği: saha kılavuzu.',
          }}
          copy={{
            en: 'From kubeconfig sprawl to zero standing privilege in three stages, with checklists, an OpenShift note, and the five questions to ask any vendor.',
            tr: 'Kubeconfig dağınıklığından sıfır kalıcı ayrıcalığa üç aşama: kontrol listeleri, OpenShift notu ve her tedarikçiye sorulacak beş soruyla.',
          }}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}
